import React, { useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useIsEnterpriseAdmin } from '../hooks/useIsEnterpriseAdmin';
import HubPageShell from '../components/home/HubPageShell';
import VeloxCard from '../components/card/VeloxCard';
import styles from './UpgradePage.module.css';

const INDIVIDUAL_PLANS = new Set(['basic', 'pro_monthly', 'pro_annual']);

function planFromSearch(params) {
  const plan = params.get('plan');
  return INDIVIDUAL_PLANS.has(plan) ? plan : 'pro_monthly';
}

function kindFromSearch(params) {
  return params.get('kind') === 'team' ? 'team' : 'individual';
}

function money(pence) {
  if (pence == null) return '—';
  const pounds = pence / 100;
  return Number.isInteger(pounds) ? `£${pounds}` : `£${pounds.toFixed(2)}`;
}

export default function OrderPage() {
  const { current } = useWorkspace();
  const { isEnterpriseAdmin, isEnterpriseUser, loading: roleLoading } = useIsEnterpriseAdmin();
  const [searchParams] = useSearchParams();
  const [kind, setKind] = useState(() => kindFromSearch(searchParams));
  const [plan, setPlan] = useState(() => planFromSearch(searchParams));
  const [includeCard, setIncludeCard] = useState(true);
  const [cardKind, setCardKind] = useState('standard');
  const [basicSeats, setBasicSeats] = useState(2);
  const [proSeats, setProSeats] = useState(0);
  const [standardCards, setStandardCards] = useState(2);
  const [customCards, setCustomCards] = useState(0);
  const [fewerCards, setFewerCards] = useState(false);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [chargingEnabled, setChargingEnabled] = useState(false);
  const [fromBasic, setFromBasic] = useState(5);
  const [fromPro, setFromPro] = useState(0);
  const [seatChange, setSeatChange] = useState(null);
  const [shopifyMessage, setShopifyMessage] = useState(null);
  const [replacementReason, setReplacementReason] = useState('');
  const [previewName, setPreviewName] = useState('Alex Rivera');
  const [previewRole, setPreviewRole] = useState('Partnerships');
  const [previewCompany, setPreviewCompany] = useState(current?.type === 'company' ? current.name : 'Your company');

  const seatTotal = Number(basicSeats) + Number(proSeats);

  const requestQuote = async () => {
    setError(null);
    try {
      const path = kind === 'individual' ? '/api/billing/quote/individual' : '/api/billing/quote/team';
      const body =
        kind === 'individual'
          ? { plan, includeCard, cardKind }
          : {
              basicSeats: Number(basicSeats) || 0,
              proSeats: Number(proSeats) || 0,
              standardCards: fewerCards ? Number(standardCards) || 0 : Number(basicSeats) + Number(proSeats),
              customCards: Number(customCards) || 0,
            };
      const res = await api.post(path, body);
      setQuote(res.data.quote);
      setChargingEnabled(!!res.data.chargingEnabled);
    } catch (e) {
      setQuote(null);
      setError(e.response?.data?.error || 'Could not quote this order');
    }
  };

  const requestSeatChange = async () => {
    setError(null);
    try {
      const res = await api.post('/api/billing/quote/seat-change', {
        from: { basicSeats: Number(fromBasic) || 0, proSeats: Number(fromPro) || 0 },
        to: { basicSeats: Number(basicSeats) || 0, proSeats: Number(proSeats) || 0 },
      });
      setSeatChange(res.data.quote);
    } catch (e) {
      setSeatChange(null);
      setError(e.response?.data?.error || 'Could not quote that seat change');
    }
  };

  const requestShopifyCards = async () => {
    setShopifyMessage(null);
    if (current?.type !== 'company' || !current.id) {
      setShopifyMessage('Switch to a company workspace to place a team card order on Shopify.');
      return;
    }
    try {
      const cards = fewerCards ? Number(standardCards) || 0 : Number(basicSeats) + Number(proSeats);
      const res = await api.post('/api/billing/shopify/team-cards', {
        enterpriseId: current.id,
        standardCards: cards,
        customCards: Number(customCards) || 0,
        replacementReason: replacementReason || null,
      });
      setShopifyMessage(
        res.data.invoiceUrl
          ? `Shopify draft created. Pay there — Velox did not take a card payment here.`
          : 'Shopify draft created. Pay on Shopify; Velox did not take a card payment here.'
      );
      if (res.data.invoiceUrl) {
        window.open(res.data.invoiceUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      const snap = e.response?.data?.quoteSnapshot;
      setShopifyMessage(
        [
          e.response?.data?.message || e.response?.data?.error || 'Could not create a Shopify draft',
          snap ? `Server price would have been ${money(snap.oneOffPence)}.` : null,
        ]
          .filter(Boolean)
          .join(' ')
      );
    }
  };

  const shortfallCopy = useMemo(() => {
    if (!quote?.cardsShortfall) return null;
    return `${quote.seats.total} seats, ${quote.cards.total} cards — ${quote.cardsShortfall} ${
      quote.cardsShortfall === 1 ? 'person' : 'people'
    } will not get a physical card.`;
  }, [quote]);

  // Membership is the existing Teams check. Hold the configurator until it
  // resolves so a personal account never sees it before the redirect.
  if (roleLoading) {
    return null;
  }
  if (isEnterpriseUser) {
    return <Navigate to="/upgrade" replace />;
  }
  if (!isEnterpriseAdmin) {
    return <Navigate to="/account" replace />;
  }

  return (
    <HubPageShell title="Order">
      <div className={styles.page}>
        <header className={styles.header}>
          <h1 className={styles.title}>Card configurator</h1>
          <p className={styles.currentPlan}>
            Preview the card, quote seats from the server, and send team plastic to Shopify.
            Stripe Checkout is not taking money.
          </p>
        </header>

        <div className={styles.configurator}>
          <div className={styles.cards}>
            <article className={styles.card}>
              <h3 className={styles.cardTitle}>Who is this for?</h3>
              <label className={styles.field}>
                <span>
                  <input
                    type="radio"
                    name="kind"
                    checked={kind === 'individual'}
                    onChange={() => setKind('individual')}
                  />{' '}
                  Individual
                </span>
              </label>
              <label className={styles.field}>
                <span>
                  <input
                    type="radio"
                    name="kind"
                    checked={kind === 'team'}
                    onChange={() => setKind('team')}
                  />{' '}
                  Team
                </span>
              </label>
            </article>

            {kind === 'individual' ? (
              <article className={styles.card}>
                <h3 className={styles.cardTitle}>Plan and card</h3>
                <label className={styles.field}>
                  Individual plan
                  <select value={plan} onChange={(e) => setPlan(e.target.value)} aria-label="Individual plan">
                    <option value="basic">Basic — free</option>
                    <option value="pro_monthly">Pro — monthly</option>
                    <option value="pro_annual">Pro — annual</option>
                  </select>
                </label>
                <label className={styles.field}>
                  <span>
                    <input
                      type="checkbox"
                      checked={includeCard}
                      onChange={(e) => setIncludeCard(e.target.checked)}
                    />{' '}
                    Add a physical card (paid on Shopify, list price)
                  </span>
                </label>
                {includeCard ? (
                  <label className={styles.field}>
                    Card kind
                    <select value={cardKind} onChange={(e) => setCardKind(e.target.value)} aria-label="Card kind">
                      <option value="standard">Standard Velox card</option>
                      <option value="custom">Custom card</option>
                    </select>
                  </label>
                ) : null}
              </article>
            ) : (
              <article className={styles.card}>
                <h3 className={styles.cardTitle}>Seats and cards</h3>
                <label className={styles.field}>
                  Members
                  <input
                    type="number"
                    min="0"
                    value={basicSeats}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      setBasicSeats(n);
                      if (!fewerCards) setStandardCards(n + Number(proSeats));
                    }}
                  />
                </label>
                <label className={styles.field}>
                  Pro licences
                  <input
                    type="number"
                    min="0"
                    value={proSeats}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      setProSeats(n);
                      if (!fewerCards) setStandardCards(n + Number(basicSeats));
                    }}
                  />
                </label>
                <label className={styles.field}>
                  <span>
                    <input
                      type="checkbox"
                      checked={fewerCards}
                      onChange={(e) => {
                        setFewerCards(e.target.checked);
                        if (!e.target.checked) setStandardCards(seatTotal);
                      }}
                    />{' '}
                    Order fewer standard cards than seats
                  </span>
                </label>
                {fewerCards ? (
                  <label className={styles.field}>
                    Standard cards
                    <input
                      type="number"
                      min="0"
                      value={standardCards}
                      onChange={(e) => setStandardCards(Number(e.target.value))}
                    />
                  </label>
                ) : null}
                <label className={styles.field}>
                  Custom cards
                  <input
                    type="number"
                    min="0"
                    value={customCards}
                    onChange={(e) => setCustomCards(Number(e.target.value))}
                  />
                </label>
                <label className={styles.field}>
                  Replacement
                  <select
                    value={replacementReason}
                    onChange={(e) => setReplacementReason(e.target.value)}
                    aria-label="Replacement reason"
                  >
                    <option value="">New cards</option>
                    <option value="loss">Replacement — loss (same price as new)</option>
                    <option value="new_hire">Replacement — new hire (same price as new)</option>
                    <option value="defect">Free — manufacturing defect</option>
                    <option value="programming_fault">Free — programming fault</option>
                  </select>
                </label>
              </article>
            )}

            <article className={styles.card}>
              <h3 className={styles.cardTitle}>Preview details</h3>
              <label className={styles.field}>
                Name
                <input value={previewName} onChange={(e) => setPreviewName(e.target.value)} />
              </label>
              <label className={styles.field}>
                Role
                <input value={previewRole} onChange={(e) => setPreviewRole(e.target.value)} />
              </label>
              <label className={styles.field}>
                Company
                <input value={previewCompany} onChange={(e) => setPreviewCompany(e.target.value)} />
              </label>
              <p className={styles.cardNote}>
                This preview is not a saved design and does not create an order.
              </p>
            </article>
          </div>

          <section className={styles.previewPane} aria-label="Card preview">
            <VeloxCard
              card={{
                name: previewName,
                role: previewRole,
                company: previewCompany,
                email: 'alex@company.com',
              }}
              variant="presentation"
            />
          </section>
        </div>

        <p>
          <button type="button" className={styles.cta} onClick={requestQuote}>
            Get a quote
          </button>
        </p>

        {error ? <p className={styles.error}>{error}</p> : null}

        {quote ? (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Quote</h2>
            <p className={styles.price}>
              Due today {money(quote.dueTodayPence)}
              {quote.recurringMonthlyPence != null ? (
                <span className={styles.per}> then {money(quote.recurringMonthlyPence)}/month</span>
              ) : quote.recurringPence ? (
                <span className={styles.per}> recurring {money(quote.recurringPence)}</span>
              ) : null}
            </p>
            {quote.band ? <p className={styles.sectionBody}>Band: {quote.band.label}</p> : null}
            {shortfallCopy ? <p className={styles.sectionBody}>{shortfallCopy}</p> : null}
            {quote.requiresManualInvoice ? (
              <p className={styles.sectionBody}>
                Provisional: this size would stop self-serve checkout. Sales would raise the invoice.
                Access starts only when that invoice is marked paid — not when this quote is shown.
              </p>
            ) : null}
            <p className={styles.cardNote}>
              {chargingEnabled
                ? 'Checkout is enabled in test mode.'
                : 'Checkout is disabled until the open commercial rules are confirmed. Nothing will be charged from this page.'}
            </p>
          </section>
        ) : null}

        {kind === 'team' ? (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Compare a seat change</h2>
            <p className={styles.sectionBody}>
              Illustrative 30-day month. Not a charge. Crossing 5→6 or 50→51 reprices every existing seat.
            </p>
            <div className={styles.cards}>
              <label className={styles.field}>
                Current Basic
                <input type="number" min="0" value={fromBasic} onChange={(e) => setFromBasic(Number(e.target.value))} />
              </label>
              <label className={styles.field}>
                Current Pro
                <input type="number" min="0" value={fromPro} onChange={(e) => setFromPro(Number(e.target.value))} />
              </label>
            </div>
            <button type="button" className={styles.cta} onClick={requestSeatChange}>
              Show bills and credits
            </button>
            {seatChange ? (
              <div className={styles.billBox}>
                <p>
                  Now {money(seatChange.before.recurringMonthlyPence)}/mo ({seatChange.before.band.label}) → then{' '}
                  {money(seatChange.after.recurringMonthlyPence)}/mo ({seatChange.after.band.label}).
                </p>
                <p>
                  Recurring change {money(seatChange.recurringDeltaPence)}/mo. Applies{' '}
                  {seatChange.effective.replace('_', ' ')}.
                </p>
                {seatChange.proration ? (
                  <p>
                    Illustrative now: charge {money(seatChange.proration.immediateChargePence)}, credit{' '}
                    {money(seatChange.proration.immediateCreditPence)}. Period-end credit{' '}
                    {money(seatChange.proration.periodEndCreditPence)}. Next period{' '}
                    {money(seatChange.proration.nextPeriodRecurringPence)}.
                  </p>
                ) : null}
                {seatChange.existingSeatsRepriced ? (
                  <p>Existing seats reprice by {money(seatChange.existingSeatsRepricedPence)}/mo at the new band.</p>
                ) : null}
              </div>
            ) : null}
            <p className={styles.cardNote}>
              Team physical cards are paid on Shopify at the server-set band price, including replacements
              that are not a Velox defect. A quote is not a purchase.
            </p>
            <button type="button" className={styles.cta} onClick={requestShopifyCards}>
              Create Shopify draft for team cards
            </button>
            {shopifyMessage ? <p className={styles.cardNote}>{shopifyMessage}</p> : null}
          </section>
        ) : null}
      </div>
    </HubPageShell>
  );
}
